import "@components/ag-grid/styles/AgCharts.css";
import { GetDailyTradeVolumeBrlData } from "@services/models/Analytics/GetDailyTradeVolumeBrl";
import { AgCharts } from "ag-charts-react";
import React, { useMemo } from "react";

type Props = {
  data: GetDailyTradeVolumeBrlData[];
};

const PayInPayOutChart: React.FC<Props> = ({ data }) => {
  const { chartOptions } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartOptions: {} };
    }

    // Sort data by date for proper chronological display
    const sortedData = [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const processedData = sortedData.map((item) => {
      const dateFormatted = new Date(item.date).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      });

      return {
        date: dateFormatted,
        originalDate: item.date,
        payIn:
          typeof item.payIn === "number"
            ? item.payIn
            : parseFloat(String(item.payIn)),
        payOut:
          typeof item.payOut === "number"
            ? item.payOut
            : parseFloat(String(item.payOut)),
      };
    });

    const options = {
      data: processedData,
      series: [
        {
          type: "bar",
          xKey: "date",
          yKey: "payIn",
          yName: "Pay-In",
          fill: "#3B82F6", // Blue color for Pay-In
          stroke: "#3B82F6",
          strokeWidth: 0,
        },
        {
          type: "bar",
          xKey: "date",
          yKey: "payOut",
          yName: "Pay-Out",
          fill: "#EF4444", // Red color for Pay-Out
          stroke: "#EF4444",
          strokeWidth: 0,
        },
      ],
      axes: [
        {
          type: "category",
          position: "bottom",
          title: {
            enabled: false,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
          },
          line: {
            enabled: false,
          },
          tick: {
            enabled: false,
          },
          gridLine: {
            enabled: false,
          },
        },
        {
          type: "number",
          position: "left",
          title: {
            enabled: false,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
            formatter: (params: any) => {
              if (params.value >= 1000000) {
                return `${(params.value / 1000000).toFixed(1)}M`;
              } else if (params.value >= 1000) {
                return `${(params.value / 1000).toFixed(1)}K`;
              }
              return params.value.toFixed(0);
            },
          },
          line: {
            enabled: false,
          },
          tick: {
            enabled: false,
          },
          gridLine: {
            enabled: false,
          },
          min: 0,
        },
      ],
      background: {
        fill: "transparent",
      },
      padding: {
        top: 20,
        bottom: 40,
        left: 40,
        right: 100,
      },
      legend: {
        enabled: true,
        position: "right",
        item: {
          marker: {
            shape: "square",
            size: 8,
          },
          label: {
            fontSize: 11,
            color: "var(--text-base)",
          },
        },
        spacing: 15,
      },
      tooltip: {
        enabled: true,
        class: "ag-chart-tooltip-custom",
        renderer: (params: any) => {
          const { datum, yKey } = params;
          const value = datum[yKey];
          const formattedValue =
            typeof value === "number" ? value.toFixed(2) : "0.00";

          return {
            content: `
              <div style="padding: 8px; background: white; border: 1px solid #ddd; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <div style="font-weight: bold; color: #333; margin-bottom: 4px;">${
                  datum.date
                }</div>
                <div style="color: ${
                  yKey === "payIn" ? "#3B82F6" : "#EF4444"
                };">
                  ${yKey === "payIn" ? "Pay-In" : "Pay-Out"}: ${formattedValue}
                </div>
              </div>
            `,
          };
        },
      },
    } as any;

    return { chartOptions: options };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] border border-border-subtle rounded-lg bg-gray-50">
        <p className="text-gray-500">No data available for chart</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[340px] border border-border-subtle rounded-lg p-4 bg-bg-page-body">
      <div className="mb-4">
        <h3 className="text-[14px] font-semibold text-text-base">
          Pay-In vs Pay-Out Trend
        </h3>
        <p className="text-[12px] text-text-subtle">
          Daily comparison of Pay-In and Pay-Out amounts
        </p>
      </div>
      <div style={{ width: "100%", height: "calc(100% - 60px)" }}>
        <AgCharts options={chartOptions} />
      </div>
    </div>
  );
};

export default PayInPayOutChart;
